import { DeleteOutline, Edit } from '@mui/icons-material';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid-pro';
import React from 'react';

import { IUserProjectRoleModel } from '../../../../../../../service/projectRole/entity/IUserProjectRoleModel';
import { IUserProjectRoleSearch } from '../../../../../../../service/projectRole/entity/IUserProjectRoleSearch';
import { UserProjectRoleAction } from '../../../../../../../service/projectRole/entity/actions/UserProjectRoleAction';
import { MultiLineText } from '../../../../../../design/dataTable/parts/fieldArea/MultiLineText';
import { DataTableUI } from '../../../../../../design/dataTable/store/DataTableUI';
import { IDataTableUI } from '../../../../../../design/dataTable/store/IDataTableUI';
import { UserProjectRoleAdminDomain } from './UserProjectRoleAdminDomain';
import { UserProjectRoleTranslationInjection } from './inject/TranslationExtensions';

export class UserProjectRoleAdminUI
  extends DataTableUI<IUserProjectRoleModel, IUserProjectRoleSearch>
  implements IDataTableUI<IUserProjectRoleModel, IUserProjectRoleSearch>
{
  public domain: UserProjectRoleAdminDomain;

  constructor(domain, rowsUI, ...params) {
    super(domain, rowsUI, ...params);
    this.domain = domain;
  }

  setDomainId(): string {
    return 'settings/role/project';
  }

  private translateActions(permissions: { data: { action: string }[] }): string {
    const translations = new Map<string, string>();
    translations.set(UserProjectRoleAction.updateFieldsProject, this.domain.i18n.translate('projectRoles.editingProjectFields'));
    translations.set(UserProjectRoleAction.updateTeamProject, this.domain.i18n.translate('projectRoles.editingProjectTeams'));
    translations.set(UserProjectRoleAction.creteApplication, this.domain.i18n.translate('projectRoles.creatingNewProfiles'));
    translations.set(UserProjectRoleAction.editApplication, this.domain.i18n.translate('projectRoles.editingProfiles'));
    translations.set(UserProjectRoleAction.reportsAccess, this.domain.i18n.translate('projectRoles.generatingReports'));
    translations.set(UserProjectRoleAction.customRequirementsAccess, this.domain.i18n.translate('projectRoles.creatingRequirements'));
    translations.set(UserProjectRoleAction.acceptCancelRequirementAccess, this.domain.i18n.translate('projectRoles.acceptingAndDeletingRequirements'));
    translations.set(UserProjectRoleAction.newMessagesNotification, this.domain.i18n.translate('projectRoles.receivingNotificationsOfNewComments'));
    translations.set(UserProjectRoleAction.showRegulators, this.domain.i18n.translate('projectRoles.viewingRequirementsOfExternalRegulatoryAuthorities'));
    translations.set(UserProjectRoleAction.jiraIntegration, this.domain.i18n.translate('projectRoles.integratingASWithJira'));
    translations.set(UserProjectRoleAction.showUnverifiedRequirements, this.domain.i18n.translate('projectRoles.managingRequirementsVerification'));
    translations.set(UserProjectRoleAction.writeRequirementRemarks, this.domain.i18n.translate('projectRoles.writeRequirementRemarks'));

    UserProjectRoleTranslationInjection.forEach((translation) => {
      translations.set(translation.value, translation.name);
    });

    return permissions.data.map((item) => translations.get(item.action)).filter(Boolean).join(', ');
  }

  async getColumns(): Promise<GridColumns> {
    this.setActionColumn();
    return [
      {
        field: 'name',
        headerName: this.domain.i18n.translate('phrases.nameTitle'),
        flex: 1,
        type: 'string',
        renderCell: (params: { value?: string }) => {
          return <MultiLineText words={this.rows.searchTerm.value} value={params.value || ''} />;
        },
      },
      {
        field: 'description',
        headerName: this.domain.i18n.translate('phrases.description'),
        flex: 1,
        type: 'string',
        renderCell: (params: { value?: string }) => {
          return <MultiLineText words={this.rows.searchTerm.value} value={params.value || ''} />;
        },
      },
      {
        field: 'permissions',
        headerName: this.domain.i18n.translate('phrases.rights'),
        flex: 1.5,
        type: 'string',
        sortable: false,
        renderCell: (params: { row: IUserProjectRoleModel }) => {
          const text = this.translateActions(params.row.permissions);
          return <MultiLineText words={this.rows.searchTerm.value} value={text || ''} />;
        },
      },
      {
        field: 'requiredUnits',
        headerName: this.domain.i18n.translate('phrases.units'),
        flex: 1,
        type: 'string',
        sortable: false,
        renderCell: (params: { row: IUserProjectRoleModel }) => {
          const units = params.row.requiredUnits;
          if (!units?.length) {
            return <></>;
          }
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'start',
              }}
            >
              <div>{units.filter((_, i) => i < 10).join(', ')}</div>
              <Chip label={units.length} variant="outlined" />
            </div>
          );
        },
      },
    ];
  }

  async setActionColumn() {
    const actionColumn = this.actionColumnSettings.value;
    actionColumn.actionItem = {
      field: 'action',
      headerName: this.domain.i18n.translate('phrases.actions'),
      flex: 0.5,
      minWidth: 100,
      headerAlign: 'center',
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (row: any) => {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              minWidth: 40,
              maxWidth: 60,
            }}
          >
            <Tooltip title={this.domain.i18n.translate('phrases.edit')}>
              <IconButton
                disabled={!this.isCanEdit.value}
                onClick={() => this.domain.router.goTo(`/settings/user/project/role/${row.row.id}/edit`)}
                style={{ marginRight: '8px' }}
              >
                <Edit sx={{ cursor: 'pointer', fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={this.domain.i18n.translate('phrases.delete')}>
              <IconButton
                disabled={!this.isCanDelete.value}
                onClick={() => this.domain.contextMenuDomain.onDeleteRow(row)}
              >
                <DeleteOutline sx={{ cursor: 'pointer', fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    };
    this.actionColumnSettings.setValue(actionColumn);
  }
}
